import classNames from 'classnames';
import { ChangeEvent, forwardRef, InputHTMLAttributes, useMemo } from 'react';
import { HiX } from 'react-icons/hi';
import { IoIosSearch } from 'react-icons/io';

import { isDefinedString } from '../../utils';
import { IconButton } from '../icon-button';

export interface SearchFieldProps
  extends Omit<
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'onChange' | 'value' | 'text'
  > {
  value?: InputHTMLAttributes<HTMLInputElement>['value'] | null;
  onClear?: (updatedValue: null) => void;
  onChange?: (updatedValue: string | null) => void;
  progressing?: boolean;
  fullWidth?: boolean;
  color?: 'light' | 'dark';
  rounded?: boolean;
}

/**
 * Prestyled search field
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>((props, ref) => {
  const { value, onClear, onChange, progressing, fullWidth, color = 'light', rounded = true, ...otherProps } = props;

  const hasSearch = useMemo(() => isDefinedString(value), [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (typeof onChange !== 'function') return;
    let updatedValue: string | null = e.target.value;
    if (!isDefinedString(updatedValue)) {
      updatedValue = null;
    }
    onChange(updatedValue);
  };

  const handleClear = () => {
    if (typeof onClear !== 'function') return;
    onClear(null);
  };

  return (
    <div className={classNames('relative w-full', !fullWidth && 'max-w-lg sm:max-w-xs')}>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        {progressing ? (
          <div className="w-5 h-5 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
        ) : (
          <IoIosSearch className="text-neutral-400" size="20" />
        )}
      </div>
      <input
        {...otherProps}
        className={classNames(
          'appearance-none border px-3 py-2 focus:outline-none focus:ring-offset-0 focus:ring-1 focus:border-brand-primary focus:ring-brand-primary', // prestyling
          color === 'light' && 'bg-[#332f2c] border-border text-white',
          color === 'dark' && 'bg-gray-800 border-gray-800 text-white',
          'block w-full shadow-sm sm:text-sm',
          'pl-10',
          'placeholder:text-secondary',
          hasSearch && 'pr-10 border-cyan-600',
          rounded ? 'rounded-full' : 'rounded-sm',
          otherProps.className,
        )}
        ref={ref}
        type="text"
        value={value ?? ''}
        onChange={handleChange}
      />
      {hasSearch && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <IconButton className="cursor-pointer" size="sm" onClick={handleClear}>
            <HiX />
          </IconButton>
        </div>
      )}
    </div>
  );
});

export default SearchField;
